import React, { useEffect, useState } from 
"react";
import {
  getClients,
  getLetters,
  deleteLetter,
  generateLetter,
  updateLetterStatus,
  getCreditReportAccounts,
  uploadCreditReport,
 deleteCreditReportAccounts,
 } from "../api";
import { useLocation } from "react-router-dom";
import AIStrategyModal from "../components/AIStrategyModal";

export default function Letters() {
  const location = useLocation();
  const queryClientId = new URLSearchParams(location.search).get("client_id");

  const [clients, setClients] = useState([]);
  const [letters, setLetters] = useState([]);
  const [filteredLetters, setFilteredLetters] = useState([]);
  const [selectedClientId, setSelectedClientId] = useState("");
  const [letterType, setLetterType] = useState("General Dispute");
  const [bureau, setBureau] = useState("");

  const [strategyAccount, setStrategyAccount] = useState(null);
  
  const [disputeItems, setDisputeItems] = useState([
    {
      creditor: "",
      accountNumber: "",
      reason: "",
    },
  ]);

  const [searchTerm, setSearchTerm] = useState("");
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [loadingClients, setLoadingClients] = useState(false);
  const [loadingLetters, setLoadingLetters] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [uploadedReport, setUploadedReport] = useState(null);

  const [parsedAccounts, setParsedAccounts] = useState([]);
  const [selectedAccounts, setSelectedAccounts] = useState([]);

  const [autoPilotOpen, setAutoPilotOpen] = useState(false);
  const [autoPilotTotal, setAutoPilotTotal] = useState(0);
  const [autoPilotCompleted, setAutoPilotCompleted] = useState(0);
  const [autoPilotCurrent, setAutoPilotCurrent] = useState("");
  const [autoPilotLog, setAutoPilotLog] = useState([]);

  const getLetterPdfUrl = (id) =>
    `${process.env.REACT_APP_API_BASE || "http://127.0.0.1:3001"}/create-letter-pdf/${id}`;

  async function loadClients() {
    try {
      setLoadingClients(true);
      setError("");

      const data = await getClients();
      const list = Array.isArray(data) ? data : [];
      setClients(list);

      setSelectedClientId((prev) => {
        const prevExists = list.some(
          (client) => String(client.id) === String(prev)
        );

        return prevExists ? prev : "";
      });
    } catch (err) {
      setError(err?.message || "Failed to load clients");
      setClients([]);
    } finally {
      setLoadingClients(false);
    }
  }

  async function loadLetters() {
    try {
      setLoadingLetters(true);
      setError("");

      const data = await getLetters();
      const list = Array.isArray(data) ? data : [];
      setLetters(list);
    } catch (err) {
      setLetters([]);
      setError(err?.message || "Failed to load letters");
    } finally {
      setLoadingLetters(false);
    }
  }

  useEffect(() => {
    loadClients();
    loadLetters();
  }, []);

  useEffect(() => {
    if (selectedClientId) {
      loadCreditReportAccounts(selectedClientId);
    }
  }, [selectedClientId]);

  useEffect(() => {
    let list = [...letters];

    if (queryClientId) {
      list = list.filter(
        (letter) => String(letter.client_id) === String(queryClientId)
      );
    }
    
    if (!queryClientId && selectedClientId) {
      list = list.filter(
        (letter) => String(letter.client_id) === String(selectedClientId)
      );
    }

    const term = searchTerm.trim().toLowerCase();

    if (term) {
      list = list.filter((letter) => {
        const clientName = (letter.full_name || "").toLowerCase();
        const type = (letter.letter_type || "").toLowerCase();
        const bureauName = (letter.bureau || "").toLowerCase();
        const content = (letter.content || "").toLowerCase();

        return (
          clientName.includes(term) ||
          type.includes(term) ||
          bureauName.includes(term) ||
          content.includes(term)
        );
      });
    }

    setFilteredLetters(list);
  }, [letters, selectedClientId, searchTerm, queryClientId]);

  async function loadCreditReportAccounts(clientId) {
    if (!clientId) return;

    try {
      const data = await getCreditReportAccounts(clientId);
      const accounts = Array.isArray(data) ? data : [];

      setParsedAccounts(
        accounts.map((account) => ({
          ...account,

          id: account.id,
          creditor: account.creditor || "Unknown Account",

          accountNumber:
            account.account_number ||
            account.accountNumber ||
            "N/A",

          type:
            account.account_type ||
            account.type ||
            "Account",

          status:
            account.status ||
            "Needs Review",

          bureau:
            account.bureau ||
            "",

          selected: true,

          risk_score:
            account.risk_score ?? 0,

          priority:
            account.priority || "Low",

          deletion_probability:
            account.deletion_probability ?? 0,

          issues:
            Array.isArray(account.issues)
              ? account.issues
              : [],

          recommended_letter:
            account.recommended_letter ||
            "General FCRA 611 Accuracy Dispute",

          next_action:
            account.next_action ||
            "Review account details before beginning a dispute",

          suggestedAction:
            account.next_action ||
            account.suggested_action ||
            account.review_reason ||
            "Review this account for possible dispute strategy.",
        }))
      );
     } catch (err) {
       console.error("Failed to load credit report accounts", err);
     }
   }

  async function generateLetterForAccount(account) {
    const selectedBureau = account.bureau || bureau || "TransUnion";

    const payload = {
      client_id: selectedClientId,
      template:
        account.recommended_letter ||
        account.letter_type ||
        "Bankruptcy Accuracy Dispute",
      letter_type:
        account.recommended_letter ||
        account.letter_type ||
        "Bankruptcy Accuracy Dispute",
      bureau: selectedBureau,
      creditor: account.creditor || "Unknown Account",
      account: account.accountNumber || account.account_number || "N/A",
      details: [
        ...(Array.isArray(account.issues)
          ? account.issues.map((issue) => `Disputed issue: ${issue}`)
          : []),

        account.review_reason
          ? `Reason for review: ${account.review_reason}`
          : "",

        account.next_action
          ? `Requested review: ${account.next_action}`
          : "",

        !account.issues?.length &&
        !account.review_reason &&
        !account.next_action
          ? (
              account.suggestedAction ||
              account.suggested_action ||
              "Please investigate this account for accuracy, completeness, and compliance."
            )
          : "",
      ]
        .filter(Boolean)
        .join("\n"),
      };

    return generateLetter(payload);
  }

  async function handleAutoPilotSelectedAccounts() {
    const selected = parsedAccounts.filter((account, index) => {
      const accountId = account.id || index;
      return selectedAccounts.includes(accountId);
    });

    if (selected.length === 0) {
      alert("Please select at least one account.");
      return;
    }

    setAutoPilotOpen(true);
    setAutoPilotTotal(selected.length);
    setAutoPilotCompleted(0);
    setAutoPilotCurrent("");
    setAutoPilotLog([]);

    if (
      !window.confirm(
        "Start AutoPilot for " + selected.length + " selected account(s)?"
      )
    ) {
      return;
    }

    try {
      setGenerating(true);
      setError("");
      setSuccessMessage("");

  let successCount = 0;
  let failCount = 0;

  for (const account of selected) {
    try {
      setAutoPilotCurrent(account.creditor || "Unknown Account");

      await generateLetterForAccount(account);

      successCount += 1;

      setAutoPilotCompleted(successCount);

      setAutoPilotLog((prev) => [
        ...prev,
        "✔ " + (account.creditor || "Unknown Account"),
      ]);
    } catch (err) {
      console.error("AutoPilot failed for account", account, err);

      failCount += 1;

      setAutoPilotLog((prev) => [
        ...prev,
        "❌ " + (account.creditor || "Unknown Account"),
      ]);
    }
  }

  await loadLetters();

      setSuccessMessage(
        "🚀 AutoPilot complete: " +
          successCount +
          " letter(s) generated" +
          (failCount ? ", " + failCount + " failed." : ".")
      );
    } catch (err) {
      setError(err?.message || "AutoPilot failed.");
    } finally {
      setGenerating(false);
    }
  }  

  async function handleGenerateLetter(e) {
    e.preventDefault();

    if (!selectedClientId) {
      setError("Please select a client first");
      return;
    }

    try {
      setGenerating(true);
      setError("");
      setSuccessMessage("");

      const client = clients.find(
        (c) => String(c.id) === String(selectedClientId)
      );

      const selectedBureau = bureau || "Experian";

      const selectedImportedAccounts = parsedAccounts.filter((account, index) => {
        const accountId = account.id || index;
        const accountBureau = String(account.bureau || selectedBureau).toLowerCase();

        return (
          selectedAccounts.includes(accountId) &&
          accountBureau === selectedBureau.toLowerCase()
        );
      });

      const disputeDetails =
        selectedImportedAccounts.length > 0
          ? selectedImportedAccounts
              .map((account, index) => {
                return (
                  index +
                  1 +
                  ". Creditor / Furnisher: " +
                  (account.creditor || "N/A") +
                  "\n   Account Number: " +
                  (account.accountNumber || "N/A") +
                  "\n   Bureau: " +
                  (account.bureau || bureau || "N/A") +
                  "\n   Type: " +
                  (account.type || "N/A") +
                  "\n   Status: " +
                  (account.status || "N/A") +
                  "\n   Reason: " +
                  (account.suggestedAction ||
                   "Please investigate this account for accuracy, completeness, and compliance.")
                );
              })
              .join("\n\n")
          : disputeItems
              .filter(
                (item) =>
                  item.creditor.trim() ||
                  item.accountNumber.trim() ||
                  item.reason.trim()
              )
              .map((item, index) => {
                return (
                  index +
                  1 +
                  ". Creditor / Furnisher: " +
                  (item.creditor || "N/A") +
                  "\n   Account Number: " +
                  (item.accountNumber || "N/A") +
                  "\n   Reason: " +
                  (item.reason ||
                    "Please investigate this account for accuracy, completeness, and compliance.")
                );
              })
              .join("\n\n");

      const checkedImportedAccounts = parsedAccounts.filter((account, index) => {
        const accountId = account.id || index;
        return selectedAccounts.includes(accountId);
      });

      if (checkedImportedAccounts.length > 0 && selectedImportedAccounts.length === 0) {
        setError(
          "No selected accounts belong to " +
            selectedBureau +
            ". Please choose " +
            selectedBureau +
            " accounts or select the correct bureau."
      );
      setGenerating(false);
      return;
    }

      const accountSummary =
        selectedImportedAccounts.length > 0
          ? selectedImportedAccounts
              .map((account) => account.accountNumber || "N/A")
              .join(", ")
          : "Multiple Accounts";

      const formData = new FormData();
      formData.append("template", letterType);
      formData.append("name", client?.full_name || "Client");
      formData.append("creditor", bureau || "Credit Bureau");
      formData.append("account", accountSummary);
      formData.append(
        "details",
        disputeDetails ||
          "Please investigate this account and verify all reported information for accuracy."
      );
      formData.append("client_id", String(selectedClientId));
      formData.append("letter_type", letterType);
      formData.append("bureau", selectedBureau);

      await generateLetter(formData);

      setSuccessMessage("Letter generated successfully from selected accounts.");
      await loadLetters();
    } catch (err) {
      setError(err?.message || "Failed to generate letter");
    } finally {
      setGenerating(false);
    }
  }

  async function handleGenerateAllBureaus() {
  if (!selectedClientId) {
    setError("Please select a client first");
    return;
  }

  try {
    setGenerating(true);
    setError("");
    setSuccessMessage("");

    const client = clients.find(
      (c) => String(c.id) === String(selectedClientId)
    );

    const checkedImportedAccounts = parsedAccounts.filter((account, index) => {
      const accountId = account.id || index;
      return selectedAccounts.includes(accountId);
    });

    if (checkedImportedAccounts.length === 0) {
      setError("Please select at least one imported account first.");
      setGenerating(false);
      return;
    }

    const bureaus = ["Experian", "Equifax", "TransUnion"];
    let generatedCount = 0;

    for (const currentBureau of bureaus) {
      const bureauAccounts = checkedImportedAccounts.filter((account) => {
        const accountBureau = String(account.bureau || "").toLowerCase();
        return accountBureau === currentBureau.toLowerCase();
      });

      if (bureauAccounts.length === 0) {
        continue;
      }

      const disputeDetails = bureauAccounts
        .map((account, index) => {
          return (
            index +
            1 +
            ". Creditor / Furnisher: " +
            (account.creditor || "N/A") +
            "\n   Account Number: " +
            (account.accountNumber || account.account_number || "N/A") +
            "\n   Bureau: " +
            currentBureau +
            "\n   Type: " +
            (account.type || account.account_type || "N/A") +
            "\n   Status: " +
            (account.status || "N/A") +
            "\n   Reason: " +
            (account.suggestedAction ||
              account.suggested_action ||
              "Please investigate this account for accuracy, completeness, and compliance.")
          );
        })
        .join("\n\n");

      const accountSummary = bureauAccounts
        .map((account) => account.accountNumber || account.account_number || "N/A")
        .join(", ");

      const formData = new FormData();

      formData.append("template", letterType);
      formData.append("letter_type", letterType);
      formData.append("name", client?.full_name || "Client");
      formData.append("client_id", String(selectedClientId));
      formData.append("creditor", currentBureau);
      formData.append("bureau", currentBureau);
      formData.append("account", accountSummary || "Multiple Accounts");
      formData.append(
        "details",
        disputeDetails ||
          "Please investigate this account and verify all reported information for accuracy."
      );

      await generateLetter(formData);
      generatedCount += 1;
    }

    if (generatedCount === 0) {
      setError(
        "No bureau-specific letters were generated. Make sure selected accounts have Experian, Equifax, or TransUnion assigned."
      );
      setGenerating(false);
      return;
    }

    setSuccessMessage(
      generatedCount + " bureau-specific letter(s) generated successfully."
    );

    await loadLetters();
  } catch (err) {
    setError(err?.message || "Failed to generate all bureau letters");
  } finally {
    setGenerating(false);
  }
}

  async function handleDeleteLetter(letterId) {
    const confirmed = window.confirm("Delete this letter?");
    if (!confirmed) return;

    try {
      setError("");
      setSuccessMessage("");

      await deleteLetter(letterId);
      setSuccessMessage("Letter deleted successfully");
      await loadLetters();
    } catch (err) {
      setError(err?.message || "Failed to delete letter");
    }
  }

  async function handleStatusChange(id, status, letter) {
    try {
      setError("");
      setSuccessMessage("");

      await updateLetterStatus(id, status);

      if (status === "verified" || status === "escalated") {
        await handleGenerateNextRound(letter);
      }

      await loadLetters();
    } catch (err) {
      setError(err?.message || "Failed to update status");
    }
  }

  async function handleGenerateNextRound(letter) {
    try {
      setError("");
      setSuccessMessage("");
      setGenerating(true);

      const currentRound = Number(letter.round || 1);
      const nextRound = currentRound + 1;

      const existingNextRound = letters.find((item) => {
        return (
          String(item.client_id) === String(letter.client_id) &&
          String(item.account_number || "") === String(letter.account_number || "") &&
          Number(item.round || 1) === nextRound
        );
     });

     if (existingNextRound) {
       setSuccessMessage(
         `Round ${nextRound} letter already exists for this account.`
       );
       return;
     }

      let nextType = "General Dispute";

      if (nextRound === 2) {
        nextType = "Method of Verification";
      } else if (nextRound >= 3) {
        nextType = "Escalation Dispute";
      }

      const formData = new FormData();

      formData.append("template", nextType);
      formData.append("letter_type", nextType);
      formData.append("round", String(nextRound));

      formData.append("client_id", String(letter.client_id));
      formData.append("bureau", letter.bureau || "Experian");
      formData.append("creditor", letter.bureau || "Experian");
      formData.append("account", letter.account_number || "N/A");
      formData.append("account_number", letter.account_number || "N/A");

      formData.append(
        "details",
        nextType === "Method of Verification"
          ? "I am requesting the method of verification used to verify this account as accurate."
          : "This account remains inaccurate, incomplete, or unverifiable. Please reinvestigate and provide a complete explanation of your findings."           
      );

      await generateLetter(formData);
      await loadLetters();

      setSuccessMessage(
        "Round " + nextRound + " " + nextType + " letter generated successfully."
      );
    } catch (err) {
      setError(err?.message || "Failed to generate next round");
    } finally {
      setGenerating(false);
    }
  }
  
const selectedClient = clients.find(
    (client) => String(client.id) === String(selectedClientId)
  );

  const selectedClientText = selectedClient
    ? `${selectedClient.full_name || `Client ${selectedClient.id}`} • ${
        selectedClient.email || "No email on file"
      }`
    : "No client selected";

    return (
      <div
        style={{
          padding: "28px",
          background: "#f3f6fb",
          minHeight: "100vh",
        }}
      >

       {autoPilotOpen ? (
         <div
           style={{
             position: "fixed",
             inset: 0,
             background: "rgba(15,23,42,.65)",
             zIndex: 9998,
             display: "flex",
             alignItems: "center",
             justifyContent: "center",
             padding: "24px",
           }}
         >
           <div
             style={{
               background: "white",
               borderRadius: "24px",
               padding: "28px",
               maxWidth: "560px",
               width: "100%",
               boxShadow: "0 30px 80px rgba(0,0,0,.30)",
             }}
           >
             <div style={{ fontSize: "26px", fontWeight: "900", marginBottom: "8px" }}>
               🚀 Integrity AI AutoPilot
             </div>

             <div style={infoLineStyle}>
               {"Processing " + autoPilotCompleted + " of " + autoPilotTotal + " accounts"}
             </div>

             <div style={infoLineStyle}>
               {"Current: " + (autoPilotCurrent || "Preparing...")}
             </div>

             <div
               style={{
                 height: "14px",
                 background: "#e5e7eb",
                 borderRadius: "999px",
                 overflow: "hidden",
                 margin: "18px 0",
               }}
             >
               <div
                 style={{
                   height: "100%",
                   width:
                     autoPilotTotal > 0
                       ? `${Math.round((autoPilotCompleted / autoPilotTotal) * 100)}%`
                       : "0%",
                   background: "#16a34a",
                 }}
               />
             </div>

             <div
               style={{
                 maxHeight: "220px",
                 overflowY: "auto",
                 display: "grid",
                 gap: "6px",
                 marginTop: "12px",
               }}
             >
               {autoPilotLog.map((item, index) => (
          <div key={index} style={infoLineStyle}>
            {item}
          </div>
        ))}
      </div>

      {autoPilotCompleted >= autoPilotTotal && autoPilotTotal > 0 ? (
        <button
          type="button"
          onClick={() => setAutoPilotOpen(false)}
          style={{
            ...primaryButtonStyle,
            background: "#0f172a",
            marginTop: "20px",
          }}
        >
          Done
        </button>
      ) : null}
    </div>
  </div>
) : null}      

       <AIStrategyModal
         account={strategyAccount}
         onClose={() => setStrategyAccount(null)}
         onGenerated={async () => {
           await loadLetters();

           if (selectedClientId) {
             await loadCreditReportAccounts(selectedClientId);
           }
         }}
       />

      <div
        style={{
          marginBottom: "28px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: "20px",
          flexWrap: "wrap",
        }}
      >
        <div>
          <div
            style={{
              fontSize: "18px",
              color: "#6b7280",
              marginBottom: "8px",
              fontWeight: "500",
            }}
          >
            Integrity Suite CRM
          </div>
          <h1
            style={{
              fontSize: "64px",
              fontWeight: "800",
              margin: 0,
              lineHeight: 1,
              color: "#0f172a",
            }}
          >
            Letters
          </h1>
        </div>

        <div style={{ display: "flex", gap: "14px", flexWrap: "wrap" }}>
          <button onClick={loadClients} style={topButtonStyle}>
            {loadingClients ? "Refreshing Clients..." : "Refresh Clients"}
          </button>

          <button onClick={loadLetters} style={topButtonStyle}>
            {loadingLetters ? "Refreshing Letters..." : "Refresh Letters"}
          </button>
        </div>
      </div>

      {error ? (
        <div
          style={{
            border: "2px solid #ff4d4f",
            borderRadius: "18px",
            padding: "18px 20px",
            marginBottom: "24px",
            background: "white",
            color: "#111827",
            fontSize: "18px",
          }}
        >
          {error}
        </div>
      ) : null}

      {successMessage ? (
        <div
          style={{
            border: "2px solid #22c55e",
            borderRadius: "18px",
            padding: "18px 20px",
            marginBottom: "24px",
            background: "white",
            color: "#111827",
            fontSize: "18px",
          }}
        >
          {successMessage}
        </div>
      ) : null}

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1.05fr 1.4fr",
          gap: "24px",
          alignItems: "start",
        }}
      >
        <div style={panelStyle}>
          <div style={panelHeaderStyle}>Generate New Letter</div>
          <div
            style={{
              fontSize: "16px",
              color: "#64748b",
              marginBottom: "20px",
            }}
          >
            Create a credit dispute letter for a selected client and bureau.
          </div>

          <form onSubmit={handleGenerateLetter}>
            <label style={labelStyle}>Client</label>
            <select
              value={selectedClientId}
              onChange={(e) => setSelectedClientId(e.target.value)}
              style={inputStyle}
            >
              <option value="">Select a client</option>
              {clients.map((client) => (
                <option key={client.id} value={String(client.id)}>
                  {(client.full_name || `Client ${client.id}`) +
                    ` (ID: ${client.id})`}
                </option>
              ))}
            </select>

            <label style={labelStyle}>Letter Type</label>
            <select
              value={letterType}
              onChange={(e) => setLetterType(e.target.value)}
              style={inputStyle}
            >
              <option value="General Dispute">General Dispute</option>
              <option value="Method of Verification">
                Method of Verification (Round 2)
              </option>
              <option value="Late Payment Dispute">Late Payment Dispute</option>
              <option value="Collection Account Dispute">
                Collection Account Dispute
              </option>
              <option value="Inquiry Dispute">Inquiry Dispute</option>
              <option value="Identity Theft Dispute">
                Identity Theft Dispute
              </option>
            </select>            

            <label style={labelStyle}>Bureau</label>
            <select
              value={bureau}
              onChange={(e) => setBureau(e.target.value)}
              style={inputStyle}
            >
              <option value="">Select Bureau</option>
              <option value="Experian">Experian</option>
              <option value="Equifax">Equifax</option>
              <option value="TransUnion">TransUnion</option>
            </select>

            <div
              style={{
                background: "#f8fbff",
                border: "1px solid #dbeafe",
                borderRadius: "18px",
                padding: "18px",
                marginBottom: "18px",
              }}
            >
              <div
                style={{
                  fontSize: "16px",
                  fontWeight: "700",
                  color: "#0f172a",
                  marginBottom: "8px",
                }}
              >
                Selected Client
              </div>
              <div
                style={{
                  fontSize: "15px",
                  color: "#64748b",
                  lineHeight: 1.6,
                }}
              >
                {selectedClientText}
              </div>
            </div>

            <div
              style={{
                display: "flex",
                gap: "12px",
                flexWrap: "wrap",
              }}
            >
              <div style={{ marginBottom: "18px", width: "100%" }}>
                <div style={labelStyle}>Imported Credit Report Accounts</div>

           <div
             style={{
               display: "flex",
               gap: "12px",
               marginTop: "12px",
               marginBottom: "16px",
               flexWrap: "wrap",
             }}
           >
             <button
               type="button"
               onClick={() => setSelectedAccounts(parsedAccounts.map((a) => a.id))}
               style={{
                 ...primaryButtonStyle,
                 background: "#475569",
               }}
             >
               ☑ Select All
             </button>

             <button
               type="button"
               onClick={() => setSelectedAccounts([])}
               style={{
                 ...primaryButtonStyle,
                 background: "#6b7280",
               }}
             >
               Clear Selection
             </button>

             <button
               type="button"
               onClick={handleAutoPilotSelectedAccounts}
               style={{
                 ...primaryButtonStyle,
                 background: "#16a34a",
               }}
             >
               🚀 AutoPilot Selected Accounts
             </button>
           </div>`

                {parsedAccounts.length === 0 ? (
                  <div
                    style={{
                      padding: "14px",
                      borderRadius: "14px",
                      border: "1px solid #e5e7eb",
                      background: "#f8fafc",
                      color: "#64748b",
                      fontSize: "15px",
                    }}
                  >
                    No imported accounts found yet. You can still use the Credit Report Import section below.
                  </div>
                ) : (
                  <div style={{ display: "grid", gap: "10px" }}>
                    {parsedAccounts.map((account, index) => {
                      const accountId = account.id || index;

                      return (
                        <label
                          key={accountId}
                          style={{
                            display: "flex",
                            alignItems: "flex-start",
                            gap: "12px",
                            padding: "14px",
                            borderRadius: "14px",
                            border: "1px solid #e5e7eb",
                            background: "#ffffff",
                            cursor: "pointer",
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={selectedAccounts.includes(accountId)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedAccounts((prev) => [...prev, accountId]);
                              } else {
                                setSelectedAccounts((prev) =>
                                  prev.filter((id) => id !== accountId)
                                );
                              }
                            }}
                            style={{ marginTop: "4px" }}
                          />

                          <div style={{ flex: 1 }}>
                            <div
                              style={{
                                fontWeight: "800",
                                color: "#0f172a",
                                marginBottom: "4px",
                              }}
                            >
                              {account.creditor || "Unknown Account"}
                            </div>

                            <div style={infoLineStyle}>
                              {"Account: " + (account.accountNumber || account.account_number || "N/A")}
                            </div>

                            <div style={infoLineStyle}>
                              {"Bureau: " + (account.bureau || "Not assigned")}
                            </div>

                            <div style={infoLineStyle}>
                              {"Type: " + (account.type || account.account_type || "Account")}
                            </div>

                            <div style={infoLineStyle}>
                              {"Status: " + (account.status || "Needs Review")}
                            </div>

                            {account.loan_type ? (
                              <div style={infoLineStyle}>
                                {"Loan Type: " + account.loan_type}
                              </div>
                            ) : null}

                            {account.date_opened ? (
                              <div style={infoLineStyle}>
                                {"Date Opened: " + account.date_opened}
                              </div>
                            ) : null}

                            {account.last_reported ? (
                              <div style={infoLineStyle}>
                                {"Last Reported: " + account.last_reported}
                              </div>
                            ) : null}

                            {account.credit_limit ? (
                              <div style={infoLineStyle}>
                                {"Credit Limit: " + account.credit_limit}
                              </div>
                            ) : null}

                            {account.high_balance ? (
                              <div style={infoLineStyle}>
                                {"High Balance: " + account.high_balance}
                              </div>
                            ) : null}

                            {account.remarks ? (
                              <div style={infoLineStyle}>
                                {"Remarks: " + account.remarks}
                              </div>
                           ) : null}

                            <div
                              style={{
                                marginTop: "12px",
                                padding: "12px",
                                borderRadius: "14px",
                                background: "#FFF8E1",
                                border: "1px solid #E6C85C",
                              }}
                            >
                              <div
                                style={{
                                  fontWeight: "900",
                                  color: "#A67C00",
                                  marginBottom: "8px",
                                }}
                              >
                                🧠 Integrity AI Analysis
                              </div>

                              <div style={infoLineStyle}>
                                {"Risk Score: " + (account.risk_score ?? "N/A") + "/100"}
                              </div>

                              <div style={infoLineStyle}>
                                {"Priority: " + (account.priority || "N/A")}
                              </div>

                              <div style={infoLineStyle}>
                                {"Deletion Probability: " + (account.deletion_probability ?? "N/A") + "%"}
                              </div>

                              <div style={infoLineStyle}>
                                {"Recommended Letter: " + (account.recommended_letter || "N/A")}
                              </div>

                              <div style={infoLineStyle}>
                                {"Next Action: " + (account.next_action || "N/A")}
                              </div>

                              {account.issues && account.issues.length > 0 ? (
                                <div style={{ marginTop: "8px" }}>
                                  <div style={{ fontWeight: "800", color: "#0f172a" }}>
                                    Issues Detected:
                                  </div>

                                  {account.issues.map((issue, issueIndex) => (
                                    <div key={issueIndex} style={infoLineStyle}>
                                      {"• " + issue}
                                    </div>
                                  ))}
                                </div>
                              ) : null}
                              <button
                                type="button"
                                onClick={() => {
                                  const strategyData = {
                                    ...account,

                                    client_id: account.client_id || selectedClientId,
                                    bureau: account.bureau || bureau || "TransUnion",
                                    creditor: account.creditor || "Unknown Account",
                                    account_number:
                                      account.account_number || account.accountNumber || "N/A",

                                    suggested_action:
                                      account.review_reason ||
                                      account.suggested_action ||
                                      account.suggestedAction ||
                                      account.next_action ||
                                      "Please investigate this account for accuracy, completeness, and compliance.",

                                    risk_score: account.risk_score ?? 0,
                                    priority: account.priority || "Low",
                                    deletion_probability: account.deletion_probability ?? 25,

                                    recommended_letter:
                                      account.recommended_letter ||
                                      account.letter_type ||
                                      "Bankruptcy Accuracy Dispute",

                                    next_action: account.next_action || "Send Round 1 dispute",

                                    issues:
                                      account.issues && account.issues.length > 0
                                        ? account.issues
                                        : ["General accuracy review"],
                                  };

                                  setStrategyAccount(strategyData);
                                  setSelectedClientId(String(strategyData.client_id));
                                  setLetterType(strategyData.recommended_letter || "General Dispute");
                                  setBureau(strategyData.bureau || "TransUnion");
                                  setSelectedAccounts([strategyData.id || strategyData.account_number]);
                                }}
                                style={{
                                  ...primaryButtonStyle,
                                  marginTop: "12px",
                                  background: "#D4AF37",
                                  color: "#111111",
                                }}
                              >
                                🧠 View AI Strategy
                              </button>
                            </div>

                            <div
                              style={{
                                fontSize: "14px",
                                fontWeight: "700",
                                color: "#A67C00",
                                marginTop: "6px",
                              }}
                            >
                              {"Suggested Action: " +
                                (account.suggestedAction ||
                                  account.suggested_action ||
                                  "Review this account for possible dispute strategy.")}
                            </div>
                          </div>
                        </label>
                      );
                    })}
                  </div>
                )}
              </div>
              <button type="submit" style={primaryButtonStyle}>
                {generating ? "Generating Letter..." : "Generate Letter"}
              </button>

              <button
                type="button"
                onClick={handleGenerateAllBureaus}
                style={{
                  ...primaryButtonStyle,
                  background: "#D4AF37",
                  color: "#111111",
                }}
              >
                {generating
                  ? "Generating Letters..."
                  : "Generate All 3 Bureaus"}
              </button>
            </div>
          </form>
          
          <div style={{ ...panelStyle, marginTop: "24px" }}>
            <div style={panelHeaderStyle}>Credit Report Import</div>
            <div style={infoLineStyle}>
              Upload an IdentityIQ, SmartCredit, Experian, Equifax, or TransUnion report.
            </div>

            <button
              type="button"
              onClick={async () => {
                if (!selectedClientId) {
                  alert("Please select a client first.");
                  return;
                }

                if (!window.confirm("Delete imported credit report accounts for this client?")) {
                  return;
                }

                try {
                  await deleteCreditReportAccounts(selectedClientId);
                  setParsedAccounts([]);
                  alert("Imported credit report accounts deleted.");
                } catch (err) {
                  alert("Failed to delete imported accounts.");
                }
              }}
              style={{
                ...primaryButtonStyle,
                background: "#dc2626",
                marginBottom: "16px",
              }}
            >
              🗑 ️ Clear Imported Accounts
            </button>

            <input
              type="file"
              accept=".pdf,.txt,.csv"
              style={inputStyle}
              onChange={async (e) => {
                const file = e.target.files?.[0] || null;
                setUploadedReport(file);

                if (!file) return;

            const formData = new FormData();

            formData.append("client_id", String(selectedClientId));
            formData.append("file", file);

            if (bureau) {
              formData.append("bureau", bureau);
            }

            const result = await uploadCreditReport(formData);

            const importedAccounts = result.accounts || [];

            setParsedAccounts(importedAccounts);

            setSuccessMessage(
              "Credit report uploaded and " +
                importedAccounts.length +
                " account(s) imported successfully."
            );
              }}
            />
            <button
              type="button"
              style={{
                ...primaryButtonStyle,
                background: "#16a34a",
              }}
            >
              Upload & Analyze Report
            </button>

            {uploadedReport && (
              <div style={infoLineStyle}>
                {"Uploaded: " + uploadedReport.name}
              </div>
            )}

            {parsedAccounts.length > 0 && (
              <div style={{ marginTop: "16px", display: "grid", gap: "12px" }}>
                <button
                  type="button"
                  onClick={() => {
                    setDisputeItems(
                      parsedAccounts.map((account) => ({
                        creditor: account.creditor || "",
                        accountNumber: account.accountNumber || account.account_number || "N/A",
                        reason:
                          account.suggestedAction ||
                          account.suggested_action ||
                          "Please investigate this account for accuracy, completeness, and compliance.",
                      }))
                    );

                    setSuccessMessage("Imported accounts added to dispute letter builder.");
                  }}
                  style={{
                    ...primaryButtonStyle,
                    background: "#0f172a",
                    marginBottom: "12px",
                  }}
                >
                  Use Imported Accounts In Letter
                </button>

                {parsedAccounts.map((account, index) => (
                  <div
                    key={index}
                    style={{
                      padding: "14px",
                      borderRadius: "14px",
                      border: "1px solid #e5e7eb",
                      background: "#f8fafc",
                    }}
                  >
                    <div style={{ fontWeight: "800", color: "#0f172a" }}>
                      {account.creditor}
                    </div>

                    <div style={infoLineStyle}>
                      {"Type: " + account.type}
                    </div>

                    <div style={infoLineStyle}>
                      {"Status: " + account.status}
                    </div>

                    <div
                      style={{
                        fontSize: "14px",
                        fontWeight: "700",
                        color: "#A67C00",
                      }}
                    >
                      {"Suggested Action: " + account.suggestedAction}
                    </div>
                  </div>
                ))}
              </div>
           )}

          </div>
        </div>

        <div style={panelStyle}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: "16px",
              flexWrap: "wrap",
              marginBottom: "18px",
            }}
          >
            <div>
              <div style={panelHeaderStyle}>Saved Letters</div>
              <div
                style={{
                  fontSize: "16px",
                  color: "#64748b",
                  marginTop: "4px",
                }}
              >
                Review, search, export, and manage all generated letters.
              </div>
            </div>

            <div
              style={{
                minWidth: "280px",
                flex: 1,
                maxWidth: "420px",
              }}
            >
              <input
                type="text"
                placeholder="Search by client, letter type, bureau, or content"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={searchInputStyle}
              />
            </div>
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
              gap: "18px",
              marginBottom: "20px",
            }}
          >
            <MiniStatCard
              title="Total Letters"
              value={letters.length}
              subtitle="All generated letters"
            />
            <MiniStatCard
              title="Showing"
              value={filteredLetters.length}
              subtitle="Current filtered results"
            />
          </div>
          <div
            style={{
              background: "#f8fbff",
              border: "1px solid #dbeafe",
              borderRadius: "20px",
              padding: "20px",
              marginBottom: "20px",
            }}
          >
            <div
              style={{
                fontSize: "22px",
                fontWeight: "800",
                color: "#0f172a",
                marginBottom: "14px",
              }}
            >
              Client Dispute Timeline
            </div>


           {filteredLetters.length === 0 ? (
             <div style={infoLineStyle}>No timeline activity yet.</div>
           ) : (
             <div style={{ display: "grid", gap: "16px" }}>
               {Object.values(
                 filteredLetters.reduce((groups, letter) => {
                   const key =
                     (letter.bureau || "Unknown") +
                     "-" +
                     (letter.creditor || "Unknown Creditor") +
                     "-" +
                     (letter.account_number || "N/A");

                   if (!groups[key]) groups[key] = [];
                   groups[key].push(letter);

                   return groups;
                 }, {})
               ).map((group, index) => (
                 <div key={index} style={{ marginBottom: "10px" }}>
                   <div
                     style={{
                       fontWeight: "800",
                       fontSize: "16px",
                       marginBottom: "8px",
                       color: "#0f172a",
                     }}
                   >
                     <>
                      <div>{group[0].bureau || "Unknown Bureau"}</div>

                      <div style={{ fontSize: "20px", marginTop: "4px" }}>
                        {group[0].creditor || "Unknown Creditor"}
                      </div>

                      <div
                        style={{
                          fontSize: "14px",
                          color: "#64748b",
                          fontWeight: "600",
                          marginTop: "2px",
                        }}
                      >
                        {"Account " + (group[0].account_number || "N/A")}
                      </div>
                    </>

                   </div>

                   <div style={{ display: "grid", gap: "10px" }}>
                     {group
                       .sort((a, b) => (a.round || 1) - (b.round || 1))
                       .map((letter) => (
                          <div
                            key={"timeline-" + letter.id}
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              gap: "12px",
                              flexWrap: "wrap",
                              padding: "14px",
                              borderRadius: "14px",
                              background: "white",
                              border: "1px solid #e5e7eb",
                            }}
                          >
                            <div>
                              <div style={{ fontWeight: "800", color: "#0f172a" }}>
                                {"Round " +
                                  (letter.round || 1) +
                                  " — " +
                                  (letter.letter_type || "General Dispute")}
                              </div>
                              <div style={infoLineStyle}>
                                {formatDate(letter.created_at)}
                              </div>
                              <div style={{ fontSize: "13px", color: "#64748b" }}>
                                {"Next: " + getNextActionText(letter)}
                              </div>
                              
                              {(() => { const recommendation = getSmartRecommendation(letter);

                              return (
                                <div
                                  style={{
                                    marginTop: "6px",
                                    fontSize: "13px",
                                    fontWeight: "800",
                                    color: recommendation.color,
                                  }}
                                >
                                  {"Smart Action: " + recommendation.label}
                                </div>
                              );
                            })()}
                            </div>

                            <div
                              style={{
                                fontWeight: "800",
                                padding: "8px 12px",
                                borderRadius: "999px",
                                fontSize: "13px",
                                background:
                                  letter.status === "verified"
                                    ? "#fef3c7"
                                    : letter.status === "escalated"
                                    ? "#f3e8ff"
                                    : letter.status === "deleted"
                                    ? "#dcfce7"
                                    : "#dbeafe",
                                color:
                                  letter.status === "verified"
                                    ? "#92400e"
                                    : letter.status === "escalated"
                                    ? "#6b21a8"
                                    : letter.status === "deleted"
                                    ? "#166534"
                                    : "#1d4ed8",
                              }}
                            >
                              {letter.status || "draft"}
                            </div>
                          </div>
                        ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
           </div>

          {loadingLetters ? (
            <div style={emptyStateStyle}>Loading letters...</div>
          ) : filteredLetters.length === 0 ? (
            <div style={emptyStateStyle}>
              {letters.length === 0
                ? "No letters generated yet."
                : "No letters matched your current filter."}
            </div>
          ) : (
            <div style={{ display: "grid", gap: "18px" }}>
              {filteredLetters.map((letter) => (
                <div key={letter.id} style={letterCardStyle}>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "start",
                      gap: "16px",
                      flexWrap: "wrap",
                      marginBottom: "12px",
                    }}
                  >
                    <div>
                      <div
                        style={{
                          fontSize: "26px",
                          fontWeight: "800",
                          color: "#0f172a",
                          marginBottom: "8px",
                        }}
                      >
                        {(letter.letter_type || "General Dispute") +
                          " (Round " +
                          (letter.round || 1) +
                          ")"}
                        </div>

                        <div style={infoLineStyle}>
                          {"Client: " + (letter.full_name || `Client ${letter.client_id}`)}
                        </div>

                        <div style={infoLineStyle}>
                          {"Bureau: " + (letter.bureau || "Not selected")}
                        </div>

                        <div style={infoLineStyle}>
                          {"Status: " + (letter.status || "draft")}
                        </div>

                        <div style={infoLineStyle}>
                          {"Next: " + getNextActionText(letter)}
                        </div>
                    
                        <div style={infoLineStyle}>
                          {"Created: " + formatDate(letter.created_at)}
                        </div>
                    </div>

                    <div
                      style={{
                        display: "flex",
                        gap: "10px",
                        flexWrap: "wrap",
                      }}
                    >
                      <a
                        href={getLetterPdfUrl(letter.id)}
                        target="_blank"
                        rel="noreferrer"
                        style={exportButtonStyle}
                      >
                        Export PDF
                      </a>

                   <button
                     type="button"
                     onClick={() => handleDeleteLetter(letter.id)}
                     style={deleteButtonStyle}
                   >
                     Delete
                   </button>

                   <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", marginTop: "10px" }}>
                     <button
                       type="button"
                       onClick={() => handleStatusChange(letter.id, "sent", letter)}
                       style={statusButtonStyle}
                     >
                       Sent
                     </button>

                     <button
                       type="button"
                       onClick={() => handleStatusChange(letter.id, "verified", letter)}
                       style={statusButtonStyle}
                     >
                       Verified
                     </button>

                     <button
                       type="button"
                       onClick={() => handleStatusChange(letter.id, "escalated", letter)}
                       style={statusButtonStyle}
                     >
                       Escalate
                     </button>

                     <button
                       type="button"
                       onClick={() => handleStatusChange(letter.id, "deleted", letter)}
                       style={statusButtonStyle}
                     >
                       Deleted
                     </button>
                   </div>
                    </div>
                  </div>
                  
                   {(letter.status || "draft") !== "deleted" &&
                  (letter.status || "draft") !== "draft" ? (
                    <button
                      type="button"
                      onClick={() => handleGenerateNextRound(letter)}
                      disabled={!canGenerateNextRound(letter)}
                      style={{
                        ...statusButtonStyle,
                        background: "#0f172a",
                        color: "white",
                        marginTop: "10px",
                        opacity: canGenerateNextRound(letter) ? 1 : 0.5,
                        cursor: canGenerateNextRound(letter) ? "pointer" : "not-allowed",
                      }}
                    >
                      {canGenerateNextRound(letter)
                        ? "Generate Next Round"
                        : "Waiting Period Active"}
                    </button>
                  ) : null}

                  <div
                    style={{
                      background: "#ffffff",
                      border: "1px solid #e5e7eb",
                      borderRadius: "18px",
                      padding: "18px",
                    }}
                  >
                    <pre
                      style={{
                        whiteSpace: "pre-wrap",
                        fontFamily: "inherit",
                        fontSize: "16px",
                        lineHeight: "1.7",
                        margin: 0,
                        color: "#111827",
                      }}
                    >
                      {letter.content}
                    </pre>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function canGenerateNextRound(letter) {

  const status = (letter.status || "draft").toLowerCase();

  if (status !== "sent") return false;
  if (!letter.next_action_date) return false;

  const today = new Date();
  const nextDate = new Date(letter.next_action_date);

  if (Number.isNaN(nextDate.getTime())) return false;

  return today >= nextDate;

}

function MiniStatCard({ title, value, subtitle }) {
  return (
    <div
      style={{
        background: "#f8fbff",
        borderRadius: "20px",
        padding: "20px",
        border: "1px solid #dbeafe",
      }}
    >
      <div
        style={{
          fontSize: "16px",
          fontWeight: "700",
          color: "#64748b",
          marginBottom: "10px",
        }}
      >
        {title}
      </div>
      <div
        style={{
          fontSize: "42px",
          fontWeight: "800",
          color: "#0f172a",
          lineHeight: 1,
          marginBottom: "8px",
        }}
      >
        {value}
      </div>
      <div
        style={{
          fontSize: "14px",
          color: "#94a3b8",
        }}
      >
        {subtitle}
      </div>
    </div>
  );
}

function formatDate(value) {
  if (!value) return "Not available";

  try {
    const cleanValue = String(value).replace("T", " ").replace("Z", "");
    const [datePart, timePart = "00:00:00"] = cleanValue.split(" ");
    const [year, month, day] = datePart.split("-").map(Number);
    const [hour, minute, second] = timePart.split(":").map(Number);

    const date = new Date(year, month - 1, day, hour, minute, second || 0);

    if (Number.isNaN(date.getTime())) return value;

    return date.toLocaleString();
  } catch (error) {
    return value;
  }
}

function getNextActionText(letter) {

  const status = (letter.status || "draft").toLowerCase();

  const round = Number(letter.round || 1);

  if (status === "draft") {

    return "Mark letter Sent to start the 30-day response period";

  }

  if (status === "deleted") {
    return "Complete — account deleted/resolved";
  }

  if (status === "verified") {
    if (round === 1) return "Send Round 2 — Method of Verification";
    if (round === 2) return "Send Round 3 — Escalation Dispute";
    return "Review for CFPB / attorney escalation";
  }

  if (status === "escalated") {
    return "Monitor response or prepare CFPB complaint";
  }

  if (!letter.next_action_date) {
    return "Waiting for bureau response";
  }

  const today = new Date();
  const nextDate = new Date(letter.next_action_date);

  if (isNaN(nextDate.getTime())) {
    return "Waiting for bureau response";
  }

  if (today >= nextDate) {
    if (round === 1) return "Ready for Round 2 — Method of Verification";
    if (round === 2) return "Ready for Round 3 — Escalation Dispute";
    return "Ready for manual review";
  }

  return "Wait until " + nextDate.toLocaleDateString();
}

function getSmartRecommendation(letter) {
  const status = (letter.status || "draft").toLowerCase();
  const round = Number(letter.round || 1);

  if (status === "draft") {
    return {
      label: "Review Letter & Mark Sent",
      color: "#2563eb",
    };
  }

  if (status === "deleted") {
    return {
      label: "Account Successfully Removed",
      color: "#16a34a",
    };
  }

  if (status === "verified") {
    if (round === 1) {
      return {
        label: "Generate Method of Verification Letter",
        color: "#f59e0b",
      };
    }

    if (round === 2) {
      return {
        label: "Generate Escalation Dispute",
        color: "#dc2626",
      };
    }

    return {
      label: "Prepare CFPB Complaint or Attorney Review",
      color: "#7c3aed",
    };
  }

  if (status === "escalated") {
    return {
      label: "Monitor Escalation Response",
      color: "#9333ea",
    };
  }

  const today = new Date();
  const nextDate = new Date(letter.next_action_date);

  if (!isNaN(nextDate.getTime()) && today >= nextDate) {
    return {
      label: "Ready For Next Dispute Round",
      color: "#2563eb",
    };
  }

  return {
    label: "Waiting For Bureau Response",
    color: "#64748b",
  };
}

const panelStyle = {
  background: "white",
  borderRadius: "24px",
  padding: "24px",
  border: "1px solid #e5e7eb",
  boxShadow: "0 10px 24px rgba(15,23,42,0.06)",
};

const panelHeaderStyle = {
  fontSize: "30px",
  fontWeight: "800",
  color: "#0f172a",
  marginBottom: "8px",
};

const labelStyle = {
  display: "block",
  marginBottom: "8px",
  fontSize: "16px",
  fontWeight: "700",
  color: "#334155",
};

const inputStyle = {
  width: "100%",
  padding: "16px 18px",
  marginBottom: "16px",
  borderRadius: "16px",
  border: "1px solid #d1d5db",
  fontSize: "17px",
  boxSizing: "border-box",
  background: "#ffffff",
};

const searchInputStyle = {
  width: "100%",
  padding: "16px 18px",
  borderRadius: "16px",
  border: "1px solid #d1d5db",
  fontSize: "16px",
  boxSizing: "border-box",
  background: "#ffffff",
};

const primaryButtonStyle = {
  flex: 1,
  minWidth: "220px",
  padding: "16px",
  borderRadius: "18px",
  border: "none",
  background: "#0f172a",
  color: "white",
  fontSize: "18px",
  fontWeight: "700",
  cursor: "pointer",
  marginTop: "4px",
};

const topButtonStyle = {
  padding: "16px 24px",
  borderRadius: "16px",
  border: "none",
  background: "#0f172a",
  color: "white",
  fontSize: "17px",
  fontWeight: "600",
  cursor: "pointer",
  boxShadow: "0 8px 20px rgba(15,23,42,0.15)",
};

const deleteButtonStyle = {
  padding: "12px 18px",
  borderRadius: "14px",
  background: "#dc2626",
  color: "white",
  border: "none",
  fontWeight: "700",
  cursor: "pointer",
};

const exportButtonStyle = {
  display: "inline-block",
  padding: "12px 18px",
  borderRadius: "14px",
  background: "#D4AF37",
  color: "#111111",
  textDecoration: "none",
  fontWeight: "700",
  cursor: "pointer",
};

const statusButtonStyle = {
  padding: "10px 14px",
  borderRadius: "12px",
  border: "1px solid #cbd5e1",
  background: "#ffffff",
  color: "#0f172a",
  fontSize: "14px",
  fontWeight: "700",
  cursor: "pointer",
};

const letterCardStyle = {
  background: "linear-gradient(180deg, #ffffff 0%, #f8fbff 100%)",
  border: "1px solid #e2e8f0",
  borderRadius: "22px",
  padding: "22px",
};

const infoLineStyle = {
  fontSize: "15px",
  color: "#64748b",
  marginBottom: "6px",
};

const emptyStateStyle = {
  padding: "28px 0",
  color: "#94a3b8",
  fontSize: "17px",
};
